import { useNavigate } from 'react-router';
import { useEffect, useMemo } from 'react';
import { isNil } from 'lodash';
import {
  useBankIDSessionsConfirmMutation,
  useBankIDSessionsQuery,
  useBankIDSessionsRejectMutation,
} from 'hooks/webapi';
import {
  BANK_ID_ACCOUNT_SELECTION,
  BANK_ID_ERROR,
  CONSENT,
  MANUAL_OCR,
  PSD2_BANK_ACCOUNT_SELECT,
} from 'Router/routes';
import { dateFormat } from 'utils/translate';
import useClientIdentificationStatusQuery from 'hooks/webapi/client/useIdentificationStatusQuery';
import { handleErrorRedirect } from '../BankIdInfo/useBankIdInfo';

export default function useBankIdConfirm() {
  const sessionId = localStorage.getItem('bankIdSessionId');
  const navigate = useNavigate();

  const { data: bankIdSessionData, isLoading: bankIdSessionIsLoading } =
    useBankIDSessionsQuery({ uuid: sessionId! }, Boolean(sessionId));
  const { mutateAsync: rejectBankId, isPending: rejecdBankIdIsPending } =
    useBankIDSessionsRejectMutation();
  const { mutateAsync: confirmBankId, isPending: confirmBankIdIsPending } =
    useBankIDSessionsConfirmMutation();

  const { data: identificationStatus } = useClientIdentificationStatusQuery();

  const handleConfirmBankID = async () => {
    if (!bankIdSessionData) {
      return;
    }

    if (!sessionId) {
      navigate(BANK_ID_ERROR, { state: { error: 'session_id_not_loaded' } });
      return;
    }

    await confirmBankId({
      uuid: sessionId,
    });

    if (
      identificationStatus?.identityStatus === 'REQUESTED' &&
      identificationStatus?.bankAccountStatus === 'APPROVED_WITH_INCOME'
    ) {
      navigate(CONSENT);
      return;
    }

    // If the user has payment accounts, navigate to the account selection page
    if (bankIdSessionData.paymentAccounts) {
      navigate(BANK_ID_ACCOUNT_SELECTION);
      return;
    }

    // If the user has no payment accounts, navigate to the PSD2 bank account selection page
    // TODO: Make sure this is what we want to do (maybe we should navigate to identification status page instead)
    navigate(PSD2_BANK_ACCOUNT_SELECT);
  };

  const handleRejectedBankID = async () => {
    if (!sessionId) {
      navigate(BANK_ID_ERROR, { state: { error: 'session_id_not_loaded' } });
      return;
    }

    await rejectBankId(sessionId);
    navigate(MANUAL_OCR);
  };

  const info = useMemo(() => {
    if (!bankIdSessionData) {
      return null;
    }

    if (bankIdSessionData?.sessionStatus !== 'SUCCESS') {
      return null;
    }

    // Extract the permanent residence address if available
    const permanentResidence = bankIdSessionData?.addresses.find(
      (address) => address.type === 'PERMANENT_RESIDENCE',
    );

    let fullAddress = '';
    if (permanentResidence) {
      const { street, streetnumber, city, zipcode, buildingapartment } =
        permanentResidence;
      const hasStreetNumber = !isNil(streetnumber);
      const hasBuildingApartment = !isNil(buildingapartment);

      // Construct the full address string conditionally
      fullAddress = `${street} ${
        hasBuildingApartment ? buildingapartment : ''
      }`;
      fullAddress += hasStreetNumber && hasBuildingApartment ? '/' : '';
      fullAddress += hasStreetNumber ? streetnumber : '';
      fullAddress += `, ${city} ${zipcode}`;
    }

    const { givenName, familyName, age, birthdate, birthnumber, idcards } =
      bankIdSessionData;

    const properIdCard = idcards.find((idcard) => idcard.type === 'ID');
    // Prepare the response object with user details
    return {
      fullName: `${givenName} ${familyName}`,
      fullAge: `${age} / ${dateFormat(birthdate)}`,
      birthnumber: birthnumber ?? '',
      cardNumber: properIdCard?.number ?? '',
      fullAddress,
    };
  }, [bankIdSessionData]);

  useEffect(() => {
    const isError = new URLSearchParams(window.location.search).has('error');

    const isSessionStatusError = !(
      bankIdSessionData?.sessionStatus === 'SUCCESS' ||
      bankIdSessionData?.sessionStatus === undefined
    );
    if (isError) {
      const errorCode = new URLSearchParams(window.location.search)
        .get('error')
        ?.toString();

      // save to handle on BankError screen
      handleErrorRedirect(navigate, errorCode, true);
      return;
    }

    if (isSessionStatusError) {
      // save to handle on BankError screen
      handleErrorRedirect(navigate, bankIdSessionData?.sessionStatus, true);
    }
  }, [navigate, bankIdSessionData?.sessionStatus]);

  return {
    isLoading: bankIdSessionIsLoading,
    isPending: confirmBankIdIsPending || rejecdBankIdIsPending,
    handleConfirmBankID,
    handleRejectedBankID,
    info,
  };
}
